import React, { FC, useContext, useEffect, useState } from 'react';
import { Auth, I18n } from 'aws-amplify';
import { Box, makeStyles, createStyles, Theme } from '@material-ui/core';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { v4 } from 'uuid';
import { ISignUpResult } from 'amazon-cognito-identity-js';
import history from '../../../history';
import Button from '../../Button';
import { LOGIN_PAGE } from '../../../constants';
import { AUTH_STATES } from '../../../constants/authConsts';
import { PortalConfigContext } from '../../../context';
import authScreensStyles from '../styles';
import ClientsClient, {
  NewClientSignUpResponse,
} from '../../../clients/ClientsClient';
import StringUtils from '../../../utils/StringUtils';
import { PasswordInputValidation } from '../../../utils/AuthUtils';
import AuthContainer from '../AuthContainer';
import CarouselLayout from '../../Carousel/CarouselLayout';
import { BaseTextField } from '../../TextField';
import RowDivider from '../../RowDivider';
import { AuthLinkActionButton } from '../AuthLinkActionButton';
import BaseTypography from '../../Text/BaseTypography';
import { useGetPortalConfigQuery } from '../../../services/api';
import LoadingOverlayer from '../../AgGrid/Overlays';
import { IAuthenticatorReturn } from '../../AwsAmplify';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    ...authScreensStyles(theme),
  }),
);

const schema = Yup.object().shape({
  fullName: Yup.string()
    .trim()
    .max(64)
    .required('Full Name is required')
    .test('is-valid-name', 'Please provide a full name', value => {
      if (!value) return false;
      return value.split(' ').length > 1;
    }),
  companyName: Yup.string()
    .trim()
    .max(100),
  email: Yup.string()
    .email('Invalid email')
    .max(100)
    .required('Email is required'),
  password: PasswordInputValidation,
  // policy: Yup.boolean().oneOf([true], 'This field must be checked'),
});

const validAuthState = ['signUp'];

const Register: FC<IAuthenticatorReturn> = props => {
  const { onStateChange, authState } = props;

  const context = useContext(PortalConfigContext);
  const classes = useStyles();

  const { data, isSuccess } = useGetPortalConfigQuery();

  const [isSignUpLoading, setIsSignUpLoading] = useState(false);

  const handleGoToSignIn = () => {
    onStateChange(AUTH_STATES.SIGN_IN);
    history.push(LOGIN_PAGE);
  };

  useEffect(() => {
    onStateChange(AUTH_STATES.SIGN_UP);
    if (context.features.signupDisabled) {
      handleGoToSignIn();
    }
  }, []);

  const areCompaniesDisabled = data?.structFields?.disableCompanies ?? false;

  if (!validAuthState.includes(authState)) return null;

  return (
    <AuthContainer>
      <Formik
        validationSchema={schema}
        initialValues={{
          fullName: '',
          password: '',
          companyName: '',
          email: '',
        }}
        onSubmit={async (values, { setStatus }) => {
          try {
            setIsSignUpLoading(true);
            const username = v4();
            const registerResponse: ISignUpResult = await Auth.signUp({
              username,
              password: values.password,
              attributes: {
                email: StringUtils.RemoveSpaceToLowerCase(values.email),
                phone_number: `+10000000000`,
                given_name: values.fullName.split(' ')[0].trim(),
                family_name: values.fullName.split(' ')[1].trim(),
                'custom:companyName': values.companyName.trim(),
              },
              clientMetadata: {
                // TODO: add fields type in PortalConfigEntity
                portal: (data as any)?.fields
                  ? (data as any)?.fields?.portalHeader
                  : '',
              },
            });
            const newClientSignUpResponse: NewClientSignUpResponse = await ClientsClient.newClientSignUp(
              username,
            );
            if (registerResponse.userConfirmed) {
              onStateChange(AUTH_STATES.SIGNED_UP, {
                username,
                password: values.password,
              });
            } else {
              onStateChange(AUTH_STATES.CONFIRM_SIGN_UP, {
                challengeName: 'CONFIRM_SIGN_UP',
                username,
                password: values.password,
                signUpPayload: newClientSignUpResponse,
              });
            }
          } catch (error) {
            setStatus({ success: false });
            setIsSignUpLoading(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values,
        }) => {
          /**
           * This will try to validate the inputs
           * on the current step in the form to determine if the user can proceed
           * @param currentStep which page of the form the user is on
           * @returns boolean if user can proceed or not
           */
          const checkNextEnabled = (currentStep: number) => {
            if (currentStep === 0) {
              // this is the first page
              return values.fullName && !errors.fullName && !errors.companyName;
            }

            if (currentStep === 1) {
              return (
                values.email &&
                !errors.email &&
                values.password &&
                !errors.password
              );
            }

            return false;
          };

          // check both steps are step correctly
          const isSignupEnabled = checkNextEnabled(0) && checkNextEnabled(1);

          return (
            <form
              noValidate
              className={classes.formContainer}
              onSubmit={handleSubmit}
            >
              {isSuccess ? (
                <CarouselLayout
                  carouselSecondaryAction={
                    <AuthLinkActionButton
                      className={classes.navigationActionsContainerMobile}
                      onClick={handleGoToSignIn}
                      linkText="Already have an account?"
                    />
                  }
                  hiddenPrev
                  length={2}
                  nextEnabled={checkNextEnabled}
                  finalActionButton={
                    <Button
                      size="large"
                      fullWidth
                      htmlId="register-create-account"
                      type="submit"
                      color="primary"
                      variant="contained"
                      isLoading={isSignUpLoading}
                      disabled={!isSignupEnabled}
                    >
                      {I18n.get('Sign Up')}
                    </Button>
                  }
                >
                  <Box>
                    <BaseTextField
                      sizeVariant="medium"
                      fullWidth
                      type="text"
                      key="fullName"
                      name="fullName"
                      autoFocus
                      variant="outlined"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.fullName}
                      label={I18n.get('Full name')}
                      error={Boolean(touched.fullName && errors.fullName)}
                      helperText={(touched.fullName && errors.fullName) || ' '}
                      autoComplete="off"
                    />
                    {!areCompaniesDisabled && (
                      <BaseTextField
                        sizeVariant="medium"
                        fullWidth
                        type="text"
                        key="companyName"
                        name="companyName"
                        variant="outlined"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.companyName}
                        label={
                          <>
                            <BaseTypography
                              fontType="13Medium"
                              component="span"
                            >
                              {I18n.get('Company')}
                            </BaseTypography>
                            <BaseTypography
                              component="span"
                              className={classes.optionalLabelHelper}
                            >
                              {` (${I18n.get('Optional')})`}
                            </BaseTypography>
                          </>
                        }
                        error={Boolean(
                          touched.companyName && errors.companyName,
                        )}
                        helperText={
                          (touched.companyName && errors.companyName) || ' '
                        }
                        autoComplete="off"
                      />
                    )}
                  </Box>
                  <Box>
                    <BaseTextField
                      sizeVariant="medium"
                      fullWidth
                      type="text"
                      key="email"
                      name="email"
                      variant="outlined"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.email}
                      label={I18n.get('Email')}
                      error={Boolean(touched.email && errors.email)}
                      helperText={(touched.email && errors.email) || ' '}
                      autoComplete="off"
                      autoFocus
                    />
                    <BaseTextField
                      sizeVariant="medium"
                      fullWidth
                      type="password"
                      key="password"
                      name="password"
                      variant="outlined"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.password}
                      label={I18n.get('Password')}
                      error={Boolean(touched.password && errors.password)}
                      helperText={(touched.password && errors.password) || ' '}
                      autoComplete="off"
                      inputProps={{
                        autoComplete: 'new-password',
                      }}
                    />
                  </Box>
                </CarouselLayout>
              ) : (
                <div className={classes.loader}>
                  <LoadingOverlayer />
                </div>
              )}
            </form>
          );
        }}
      </Formik>
      <RowDivider className={classes.divider} mt={2.5} mb={1.5} />
      <AuthLinkActionButton
        className={classes.navigationActionsContainerDesktop}
        onClick={handleGoToSignIn}
        linkText="Already have an account?"
      />
    </AuthContainer>
  );
};

export default Register;
